/**
 * @fileoverview KOLs page route for the Onicorn application
 * @module routes/kols
 */

import type { ReactElement } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { BackgroundShape } from '@/components/ui/BackgroundShape';
import { KOLsTabs } from '@/features/kols';

/**
 * KOLs (Key Opinion Leaders) page component.
 *
 * Main KOL marketplace page that displays available Key Opinion Leaders
 * for project promotions and influencer collaborations.
 *
 * Features:
 * - Background lamp light effect
 * - Header with title and subtitle
 * - KOL listing (placeholder content)
 *
 * @component
 */
function KOLsPage(): ReactElement {
  return (
    <div className="relative min-h-screen bg-background flex flex-col">
      {/* Background Shape */}
      <BackgroundShape />

      {/* Main Content */}
      <div className="relative z-10 w-full flex flex-col flex-1">
        {/* Header Section - Mobile Responsive */}
        <header className={cn(
          'text-center',
          'mb-10 md:mb-[60px]',
          'pt-20 md:pt-[140px]',
          'mx-4 md:mx-0'  // Match landing page horizontal margins
        )}>
          <h1 className={cn(
            'text-onicorn-green font-bold mb-2',
            'text-[28px] leading-[32px]',
            'md:text-[36px] md:leading-[40px]',
            'font-sans'
          )}>
            KOL Marketplace
          </h1>

          <p className={cn(
            'text-onicorn-gray-400',
            'text-[14px] leading-[20px]',
            'md:text-[16px] md:leading-[24px]',
            'font-normal font-sans',
            'max-w-[435px] mx-auto'
          )}>
            Discover Key Opinion Leaders available for project promotions and influencer collaborations.
          </p>
        </header>

        {/* Tabs Section with semantic border positioning */}
        <div className={cn(
          'max-w-[1300px] relative',
          'mx-4 md:mx-auto'  // Match landing page horizontal margins
        )}>
          {/* Top border - aligns with VerticalBorders - Desktop Only */}
          <div
            className="hidden md:block absolute top-0 h-px bg-onicorn-gray-500 z-40"
            style={{
              left: 'calc(50% - 650px)',
              right: 'calc(50% - 650px)'
            }}
          />

          {/* Bottom border positioned at the bottom of the "All KOLs" tab - Desktop Only */}
          <div
            className="hidden md:block absolute h-px bg-onicorn-gray-500 z-40"
            style={{
              top: '54px', // Tab height
              left: 'calc(50% - 650px)',
              right: 'calc(50% - 650px)'
            }}
          />

          {/* Additional left vertical border - extended upward, 110px away from main left border - Desktop Only */}
          {/* Height: from -14rem above container to 54px (horizontal line position) = 14rem + 54px */}
          <div
            className="hidden md:block absolute w-px bg-onicorn-gray-500 z-0"
            style={{
              top: 'calc(-14rem)',
              height: 'calc(14rem + 54px)',
              left: 'calc(50% - 221.75px - 110px)' // 110px left of left border
            }}
          />

          {/* Additional right vertical border - extended upward, 110px away from main right border - Desktop Only */}
          {/* Height: from -14rem above container to 54px (horizontal line position) = 14rem + 54px */}
          <div
            className="hidden md:block absolute w-px bg-onicorn-gray-500 z-0"
            style={{
              top: 'calc(-14rem)',
              height: 'calc(14rem + 54px)',
              right: 'calc(50% - 221.75px - 110px)' // 110px right of right border
            }}
          />

          <KOLsTabs className="mb-5" />
        </div>
      </div>
    </div>
  );
}

/**
 * KOLs route configuration.
 *
 * Defines the KOLs route ('/kols') that displays key opinion leaders
 * and their insights in the Web3 space.
 */
export const Route = createFileRoute('/kols/')({
  component: KOLsPage,
});
